import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Badge,
  Banner,
  Breadcrumbs,
  Button,
  Combobox,
  DatePicker,
  Empty,
  InputGroup,
  LayerCard,
  Loader,
  Popover,
  Table,
  useKumoToastManager,
  type DateRange,
} from '@cloudflare/kumo'
import {
  HouseIcon,
  BuildingsIcon,
  CalendarDotsIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  XIcon,
} from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import type { AdminRoomView, Building, RoomBookingDto } from '@hub-wifi/shared'

interface ListResp {
  items: RoomBookingDto[]
  nextCursor: string | null
}

function startOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function endOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(23, 59, 59, 999)
  return x
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0)
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999)
}

function today(): Date {
  return new Date()
}

/** Format the booking filters' date-range Popover trigger label. */
function formatRangeLabel(
  range: DateRange | undefined,
  locale: string,
  t: (key: string, params?: Record<string, string>) => string,
): string {
  if (!range?.from) return t('bookings.filter.allDates')
  const fromStr = range.from.toLocaleDateString(locale)
  if (!range.to) return t('bookings.filter.fromOnwards', { date: fromStr })
  return `${fromStr} – ${range.to.toLocaleDateString(locale)}`
}

interface Filters {
  roomId: string
  range: DateRange | undefined
}

interface RoomOption {
  label: string
  value: string
}

/**
 * Translate a booking API error to a user-facing string.
 *
 * The booking routes return `{ error: <code>, code: <code>, message? }`,
 * so `ApiError.message` is the static code (e.g. `'BUILDING_MISMATCH'`).
 * Prefer a `bookings.errors.<code>` translation; fall back to body.message,
 * then to a generic error string.
 */
function describeBookingError(err: unknown, t: (key: string) => string): string {
  if (err instanceof ApiError) {
    const body = (err.body ?? {}) as { code?: string; message?: string }
    const code = body.code ?? err.message
    const translated = t(`bookings.errors.${code}`)
    if (translated && translated !== `bookings.errors.${code}`) return translated
    if (body.message) return body.message
    return code || t('common.error')
  }
  return t('common.error')
}

export function BuildingBookingsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()
  const { t, locale } = useTranslation()
  const toasts = useKumoToastManager()

  const [building, setBuilding] = useState<Building | null>(null)
  const [rooms, setRooms] = useState<AdminRoomView[]>([])
  const [items, setItems] = useState<RoomBookingDto[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  // Default view lists the next 30 days (range.from = start of today,
  // range.to = end of day 29 days from now). The "all" preset clears range
  // entirely to reveal past bookings.
  const [filters, setFilters] = useState<Filters>({
    roomId: '',
    range: { from: startOfDay(today()), to: endOfDay(addDays(today(), 29)) },
  })
  // Display-only: which preset the trigger label names; null = custom range.
  const [presetKey, setPresetKey] = useState<string | null>('next30')
  const [searchValue, setSearchValue] = useState('')
  const [loading, setLoading] = useState(true)
  const [listLoading, setListLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cancelTarget, setCancelTarget] = useState<RoomBookingDto | null>(null)
  const [cancelBusy, setCancelBusy] = useState(false)

  const fetchList = useCallback(
    async (reset = true, override?: Partial<Filters>) => {
      if (!id) return
      const eff = { ...filters, ...override }
      setListLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const qs = new URLSearchParams()
        if (eff.roomId) qs.set('roomId', eff.roomId)
        const from = eff.range?.from ? startOfDay(eff.range.from).toISOString() : undefined
        const to = eff.range?.to ? endOfDay(eff.range.to).toISOString() : undefined
        if (from) qs.set('from', from)
        if (to) qs.set('to', to)
        if (!reset && nextCursor) qs.set('cursor', nextCursor)
        const resp = await apiRequest<ListResp>(`/api/buildings/${id}/bookings?${qs.toString()}`, {
          token,
        })
        setItems((prev) => (reset ? resp.items : [...prev, ...resp.items]))
        setNextCursor(resp.nextCursor)
        setError(null)
      } catch (err) {
        setError(describeBookingError(err, t))
      } finally {
        setListLoading(false)
      }
    },
    [id, getAccessTokenSilently, filters, nextCursor, t],
  )

  const presets: Array<{ key: string; range: DateRange | undefined }> = [
    { key: 'upcoming', range: { from: startOfDay(today()) } },
    { key: 'today', range: { from: startOfDay(today()), to: endOfDay(today()) } },
    { key: 'next7', range: { from: startOfDay(today()), to: endOfDay(addDays(today(), 6)) } },
    { key: 'thisMonth', range: { from: startOfMonth(today()), to: endOfMonth(today()) } },
    { key: 'next30', range: { from: startOfDay(today()), to: endOfDay(addDays(today(), 29)) } },
    { key: 'all', range: undefined },
  ]

  function applyPreset(key: string | null, range: DateRange | undefined) {
    setPresetKey(key)
    setFilters((f) => ({ ...f, range }))
    void fetchList(true, { range })
  }

  // Initial load: building + rooms, then bookings.
  useEffect(() => {
    if (!id) return
    let cancelled = false
    async function init() {
      try {
        const token = await getAccessTokenSilently()
        const [b, rs] = await Promise.all([
          apiRequest<Building>(`/api/buildings/${id}`, { token }),
          apiRequest<AdminRoomView[]>(`/api/buildings/${id}/rooms`, { token }),
        ])
        if (cancelled) return
        setBuilding(b)
        setRooms(rs)
      } catch (err) {
        if (!cancelled) setError(describeBookingError(err, t))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void init()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Fetch list on initial mount + when an Apply button is clicked.
  // We deliberately only trigger on `id` change to avoid refetching on every
  // filter keystroke. `fetchList` is invoked manually via the Apply button.
  useEffect(() => {
    if (!id) return
    void fetchList(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Client-side search across coworker name, email, and company (server
  // filter not supported).
  const q = searchValue.trim().toLowerCase()
  const filteredItems = q
    ? items.filter((b) =>
        [b.coworkerName, b.coworkerEmail, b.coworkerCompany].some((v) =>
          (v ?? '').toLowerCase().includes(q),
        ),
      )
    : items

  async function confirmCancel() {
    if (!id || !cancelTarget) return
    setCancelBusy(true)
    try {
      const token = await getAccessTokenSilently()
      await apiRequest(`/api/buildings/${id}/bookings/${cancelTarget.id}/cancel`, {
        method: 'POST',
        token,
        body: {},
      })
      toasts.add({ variant: 'success', title: t('bookings.cancelSuccess') })
      setCancelTarget(null)
      void fetchList(true)
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: describeBookingError(err, t),
      })
    } finally {
      setCancelBusy(false)
    }
  }

  const roomOptions: RoomOption[] = [
    { label: t('bookings.filter.allRooms'), value: '' },
    ...rooms.map((r) => ({ label: r.name, value: r.id })),
  ]
  const selectedRoomOption = roomOptions.find((o) => o.value === filters.roomId) ?? roomOptions[0]
  const headCls = 'py-1 px-2 text-xs font-medium text-kumo-subtle bg-kumo-base'

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link icon={<BuildingsIcon size={14} />} href="/buildings">
              {t('buildings.title')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>
              {building?.name ?? '...'} - {t('bookings.title')}
            </Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('bookings.title')}
        description={building?.name ?? undefined}
        tabs={[
          { label: t('bookings.tab.list'), value: 'list' },
          { label: t('bookings.tab.calendar'), value: 'calendar' },
        ]}
        defaultTab="list"
        onValueChange={(v) => {
          if (v === 'calendar') navigate(`/buildings/${id}/calendar`)
        }}
      >
        <Link to={`/buildings/${id}/bookings/new`}>
          <Button variant="primary" size="sm" icon={<PlusIcon size={16} />}>
            {t('bookings.newBooking')}
          </Button>
        </Link>
      </PageHeader>

      {error && (
        <Banner variant="error" className="mb-3">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <InputGroup className="w-full max-w-xs">
          <InputGroup.Addon>
            <MagnifyingGlassIcon />
          </InputGroup.Addon>
          <InputGroup.Input
            value={searchValue}
            placeholder={t('bookings.filter.searchPlaceholder')}
            aria-label={t('bookings.filter.search')}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          {searchValue && (
            <InputGroup.Addon align="end" className="pr-1">
              <InputGroup.Button
                shape="square"
                icon={XIcon}
                aria-label={t('common.clear')}
                onClick={() => setSearchValue('')}
              />
            </InputGroup.Addon>
          )}
        </InputGroup>

        <Combobox
          items={roomOptions}
          value={selectedRoomOption}
          onValueChange={(v) => {
            const opt = v as RoomOption | null
            const roomId = opt?.value ?? ''
            setFilters((f) => ({ ...f, roomId }))
            void fetchList(true, { roomId })
          }}
          isItemEqualToValue={(a, b) => a.value === b.value}
        >
          <Combobox.TriggerValue className="w-40" placeholder={t('bookings.filter.room')} />
          <Combobox.Content>
            <Combobox.Input placeholder={t('bookings.filter.room')} />
            <Combobox.Empty>{t('common.noResults')}</Combobox.Empty>
            <Combobox.List>
              {(item: RoomOption) => (
                <Combobox.Item key={item.value} value={item}>
                  {item.label}
                </Combobox.Item>
              )}
            </Combobox.List>
          </Combobox.Content>
        </Combobox>

        <Popover>
          <Popover.Trigger
            render={<Button variant="secondary" icon={<CalendarDotsIcon size={16} />} />}
          >
            {presetKey
              ? t(`bookings.preset.${presetKey}`)
              : formatRangeLabel(filters.range, locale, t)}
          </Popover.Trigger>
          <Popover.Content>
            <div className="flex items-start gap-3">
              <div className="flex flex-col gap-1 w-40 border-r border-kumo-line pr-3">
                {presets.map((p) => (
                  <Popover.Close
                    key={p.key}
                    render={<Button variant="ghost" size="sm" className="w-full text-left" />}
                    onClick={() => applyPreset(p.key, p.range)}
                  >
                    {t(`bookings.preset.${p.key}`)}
                  </Popover.Close>
                ))}
              </div>
              <div className="flex flex-col gap-2 pl-3">
                <DatePicker
                  mode="range"
                  numberOfMonths={2}
                  selected={filters.range}
                  onChange={(r) => {
                    setFilters((f) => ({ ...f, range: r }))
                    setPresetKey(null)
                  }}
                />
                <div className="flex justify-end">
                  <Popover.Close
                    render={<Button variant="primary" size="sm" loading={listLoading} />}
                    onClick={() => void fetchList(true)}
                  >
                    {t('common.apply')}
                  </Popover.Close>
                </div>
              </div>
            </div>
          </Popover.Content>
        </Popover>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && filteredItems.length === 0 && !listLoading && (
        <Empty
          icon={<BuildingsIcon size={48} className="text-kumo-inactive" />}
          title={t('bookings.empty')}
        />
      )}

      {!loading && filteredItems.length > 0 && (
        <LayerCard>
          <LayerCard.Primary className="p-0">
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <Table data-testid="bookings-table">
                <Table.Header>
                  <Table.Row>
                    <Table.Head className={headCls}>{t('bookings.col.start')}</Table.Head>
                    <Table.Head className={headCls}>{t('bookings.col.end')}</Table.Head>
                    <Table.Head className={headCls}>{t('bookings.col.room')}</Table.Head>
                    <Table.Head className={headCls}>{t('bookings.col.company')}</Table.Head>
                    <Table.Head className={headCls}>{t('bookings.col.coworker')}</Table.Head>
                    <Table.Head className={headCls}>{t('bookings.col.status')}</Table.Head>
                    <Table.Head className={headCls}>{t('bookings.col.actions')}</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredItems.map((b) => (
                    <Table.Row key={b.id} data-testid={`booking-row-${b.id}`}>
                      <Table.Cell>{new Date(b.startAt).toLocaleString()}</Table.Cell>
                      <Table.Cell>{new Date(b.endAt).toLocaleString()}</Table.Cell>
                      <Table.Cell>{b.roomName ?? b.roomId}</Table.Cell>
                      <Table.Cell>
                        {b.coworkerCompanyId && b.coworkerCompany ? (
                          <Link
                            to={`/companies/${b.coworkerCompanyId}/edit`}
                            className="text-kumo-link underline"
                          >
                            {b.coworkerCompany}
                          </Link>
                        ) : (
                          (b.coworkerCompany ?? t('common.b2c'))
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-col">
                          <span>{b.coworkerName ?? b.coworkerId}</span>
                          {b.coworkerEmail && (
                            <span className="text-xs text-kumo-subtle">{b.coworkerEmail}</span>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge variant={b.status === 'confirmed' ? 'primary' : 'secondary'}>
                          {t(`bookings.status.${b.status}`)}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        {b.status === 'confirmed' && (
                          <div className="flex gap-2">
                            <Link to={`/buildings/${id}/bookings/${b.id}/edit`}>
                              <Button variant="ghost" size="sm">
                                {t('common.edit')}
                              </Button>
                            </Link>
                            <Button
                              variant="secondary-destructive"
                              size="sm"
                              onClick={() => setCancelTarget(b)}
                            >
                              {t('bookings.cancel')}
                            </Button>
                          </div>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </LayerCard.Primary>
        </LayerCard>
      )}

      {nextCursor && (
        <div className="flex justify-center pt-2">
          <Button variant="secondary" onClick={() => void fetchList(false)} loading={listLoading}>
            {t('common.loadMore')}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(next) => {
          if (!next) setCancelTarget(null)
        }}
        title={t('bookings.confirmCancel')}
        description={t('bookings.confirmCancelBody')}
        confirmLabel={t('bookings.confirmCancelAction')}
        cancelLabel={t('common.back')}
        loading={cancelBusy}
        onConfirm={() => void confirmCancel()}
      />
    </div>
  )
}
